import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import { Directive, input, Host, Self, OnInit } from '@angular/core';
import { DEFAULT_CDK_OVERLAY_CONNECTED_POSITIONS } from '../../consts/cdk-overlay-connected-positions.const';

/**
 * A directive that dynamically inverts `CdkConnectedOverlay` offsets when the overlay flips
 * along the X or Y axis, removing the need to define all position variations manually.
 *
 * @example
 * ```html
 * <ng-template
 *   cdkConnectedOverlay
 *   pcCdkOverlayDynamicOffsetY="10"
 *   pcCdkOverlayDynamicOffsetX="20">
 * </ng-template>
 * ```
 *
 * @inputs
 * - `pcCdkOverlayDynamicOffsetY`: Adjusts vertical offset based on `overlayY`.
 * - `pcCdkOverlayDynamicOffsetX`: Adjusts horizontal offset based on `overlayX`.
 */

@Directive({
  standalone: true,
  selector: '[cdkConnectedOverlay][pcCdkOverlayDynamicOffsetY], [cdkConnectedOverlay][pcCdkOverlayDynamicOffsetX]'
})
export class CdkOverlayDynamicOffsetDirective implements OnInit {
  dynamicOffsetY = input(0, { alias: 'pcCdkOverlayDynamicOffsetY' });
  dynamicOffsetX = input(0, { alias: 'pcCdkOverlayDynamicOffsetX' });

  constructor(@Host() @Self() public cdkOverlayHost: CdkConnectedOverlay) {}

  ngOnInit(): void {
    if (!this.cdkOverlayHost) return;

    this.cdkOverlayHost.positions ??= DEFAULT_CDK_OVERLAY_CONNECTED_POSITIONS;
    this.cdkOverlayHost.positions = setDynamicOffsets(
      this.cdkOverlayHost.positions,
      this.dynamicOffsetY(),
      this.dynamicOffsetX()
    );
  }
}

export function setDynamicOffsets(
  positions: ConnectedPosition[],
  offsetY: number,
  offsetX: number
): ConnectedPosition[] {
  return positions.map((position) => ({
    ...position,
    offsetY: offsetY * (position.overlayY === 'top' ? 1 : -1),
    offsetX: offsetX * (position.overlayX === 'start' ? 1 : -1)
  }));
}
